import { useEffect, useState, useRef } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import api from "@/lib/api";
import { can, useAuth } from "@/lib/auth";
import { toast } from "sonner";
import JsBarcode from "jsbarcode";
import { QRCodeSVG } from "qrcode.react";
import { ArrowLeft, Edit, Trash2, ArrowDownToLine, ArrowUpFromLine, Tag } from "lucide-react";

export default function ProductDetailPage() {
  const { id } = useParams();
  const nav = useNavigate();
  const { user } = useAuth();
  const [p, setP] = useState(null);
  const [movs, setMovs] = useState([]);
  const barcodeRef = useRef(null);

  const load = async () => {
    const [r1, r2] = await Promise.all([
      api.get(`/products/${id}`),
      api.get("/movements", { params: { product_id: id, limit: 100 } }),
    ]);
    setP(r1.data); setMovs(r2.data);
  };

  useEffect(() => { load(); /* eslint-disable-next-line */ }, [id]);

  useEffect(() => {
    if (p && barcodeRef.current) {
      try {
        JsBarcode(barcodeRef.current, p.codice_interno, {
          format: "CODE128", height: 50, width: 1.6,
          background: "#121212", lineColor: "#4CAF50", fontSize: 12, font: "JetBrains Mono",
        });
      } catch {}
    }
  }, [p]);

  const onDelete = async () => {
    if (!window.confirm("Eliminare definitivamente questo prodotto e tutti i suoi movimenti?")) return;
    await api.delete(`/products/${id}`);
    toast.success("Prodotto eliminato");
    nav("/prodotti");
  };

  if (!p) return <div className="p-8 text-zinc-500 uppercase tracking-widest text-sm">Caricamento...</div>;

  const stockColor = p.giacenza === 0 ? "text-[#FF3B30]" : p.giacenza <= p.quantita_minima ? "text-[#FF9F0A]" : "text-[#4CAF50]";

  return (
    <div className="p-4 md:p-8 max-w-6xl mx-auto">
      <button onClick={()=>nav(-1)} className="text-xs uppercase tracking-widest text-zinc-400 hover:text-white mb-3 flex items-center gap-1.5">
        <ArrowLeft size={14} /> Indietro
      </button>

      <div className="flex items-start justify-between flex-wrap gap-4 mb-6">
        <div>
          <div className="font-mono-data text-sm text-[#4CAF50] mb-1">{p.codice_interno}</div>
          <h1 className="font-display text-3xl md:text-4xl font-black uppercase tracking-tight">{p.descrizione}</h1>
          <div className="text-zinc-400 mt-1">{p.marca} • {p.categoria}</div>
        </div>
        <div className="flex flex-wrap gap-2">
          {can(user, "carico") && <Link to={`/carico?product=${p.id}`} className="gm-btn-primary h-11 px-4 rounded-sm flex items-center gap-2"><ArrowDownToLine size={16} /> Carico</Link>}
          {can(user, "scarico") && <Link to={`/scarico?product=${p.id}`} className="h-11 px-4 border border-zinc-700 hover:border-white text-white uppercase text-xs tracking-widest font-bold rounded-sm flex items-center gap-2"><ArrowUpFromLine size={16} /> Scarico</Link>}
          {can(user, "edit_product") && <Link to={`/prodotti/${p.id}/modifica`} className="h-11 px-4 border border-zinc-700 hover:border-white text-white uppercase text-xs tracking-widest font-bold rounded-sm flex items-center gap-2"><Edit size={16} /> Modifica</Link>}
          {user.role === "admin" && <button onClick={onDelete} className="h-11 px-4 border border-[#FF3B30]/40 text-[#FF3B30] hover:bg-[#FF3B30]/10 uppercase text-xs tracking-widest font-bold rounded-sm flex items-center gap-2" data-testid="delete-product-button"><Trash2 size={16} /></button>}
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-5 mb-6">
        <div className="gm-card p-5">
          <div className="text-[10px] uppercase tracking-[0.18em] text-zinc-500 mb-2">Giacenza attuale</div>
          <div className={`font-display text-6xl font-black ${stockColor}`}>{p.giacenza}</div>
          <div className="text-xs text-zinc-500 mt-2 font-mono-data">Soglia min: {p.quantita_minima}</div>
        </div>
        <div className="gm-card p-5">
          <div className="text-[10px] uppercase tracking-[0.18em] text-zinc-500 mb-2">Posizione</div>
          <div className="font-mono-data text-3xl font-bold text-white">{p.posizione || "—"}</div>
          <div className="text-xs text-zinc-500 mt-2">Zona-Scaffale-Ripiano</div>
        </div>
        <div className="gm-card p-5">
          <div className="text-[10px] uppercase tracking-[0.18em] text-zinc-500 mb-2">Valore</div>
          <div className="font-display text-3xl font-bold text-white">€ {(p.giacenza * p.prezzo_acquisto).toFixed(2)}</div>
          <div className="text-xs text-zinc-500 mt-2 font-mono-data">€ {p.prezzo_acquisto.toFixed(2)} / pz</div>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-5 mb-6">
        <div className="gm-card p-5 lg:col-span-2">
          <h3 className="font-display text-xl font-bold uppercase mb-4 tracking-tight">Dettagli</h3>
          <dl className="grid grid-cols-1 md:grid-cols-2 gap-x-6 gap-y-3 text-sm">
            <Row k="Codice fornitore" v={<span className="font-mono-data">{p.codice_fornitore}</span>} />
            <Row k="Compatibilità" v={p.compatibilita || "—"} />
            <Row k="Note" v={p.note || "—"} />
            <Row k="Creato" v={new Date(p.created_at).toLocaleString("it-IT")} />
          </dl>
        </div>
        <div className="gm-card p-5 flex flex-col items-center">
          <h3 className="font-display text-xl font-bold uppercase mb-3 tracking-tight self-start flex items-center gap-2"><Tag size={16}/> Codici</h3>
          <svg ref={barcodeRef} className="mb-3" />
          <QRCodeSVG value={p.codice_interno} size={120} bgColor="#121212" fgColor="#4CAF50" />
          <Link to={`/etichette?id=${p.id}`} className="mt-4 text-xs uppercase tracking-widest text-[#4CAF50] hover:underline" data-testid="generate-label-link">
            → Stampa etichetta PDF
          </Link>
        </div>
      </div>

      {p.foto && (
        <div className="gm-card p-5 mb-6">
          <h3 className="font-display text-xl font-bold uppercase mb-3 tracking-tight">Foto</h3>
          <img src={p.foto} alt={p.descrizione} className="max-h-80 object-contain" />
        </div>
      )}

      <div className="gm-card overflow-hidden">
        <div className="p-5 border-b border-zinc-800">
          <h3 className="font-display text-xl font-bold uppercase tracking-tight">Storico movimenti</h3>
        </div>
        <div className="overflow-x-auto">
          <table className="gm-table">
            <thead>
              <tr><th>Data/Ora</th><th>Tipo</th><th className="text-right">Q.tà</th><th className="text-right">Giac. Dopo</th><th>Causale/Doc.</th><th>Utente</th></tr>
            </thead>
            <tbody>
              {movs.length === 0 && (<tr><td colSpan="6" className="text-center text-zinc-500 py-6">Nessun movimento</td></tr>)}
              {movs.map(m => (
                <tr key={m.id}>
                  <td className="font-mono-data text-xs text-zinc-400">{new Date(m.created_at).toLocaleString("it-IT")}</td>
                  <td>
                    {m.tipo === "carico" && <span className="gm-badge gm-badge-success">Carico</span>}
                    {m.tipo === "scarico" && <span className="gm-badge gm-badge-warning">Scarico</span>}
                    {m.tipo === "rettifica" && <span className="gm-badge gm-badge-muted">Rettifica</span>}
                  </td>
                  <td className="text-right font-mono-data font-bold">{m.tipo === "scarico" ? "-" : m.tipo === "carico" ? "+" : "="}{m.quantita}</td>
                  <td className="text-right font-mono-data">{m.giacenza_dopo}</td>
                  <td className="text-zinc-400 text-xs">{m.documento || m.causale || m.commessa || "—"}</td>
                  <td className="text-zinc-400 text-xs">{m.user_name}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}

const Row = ({k,v}) => (
  <>
    <dt className="text-zinc-500 uppercase text-[10px] tracking-widest">{k}</dt>
    <dd className="text-white">{v}</dd>
  </>
);
